<template>
  <div class="footerContainer ma-0" :class="{ footerContainerMobile: $vuetify.breakpoint.mdAndDown }">
    <div class="footerCap">&nbsp;</div>
    <v-footer color="#200c40" class="footer ma-0 pa-0">
      <v-btn
        dark
        width="105"
        height="110"
        class="mb-0 text-capitalize sigfloButtonSmall400 previousButton mt-1"
        color="#2B1056"
        :disabled="!showPrevButton"
        @click="previousStep"
      >
        <v-icon small>mdi-arrow-left</v-icon>
        <div>Back</div>
      </v-btn>

      <div ref="scrollableFooter" class="timelineBottom footerDiv sigfloScrollbar d-flex pt-4">
        <div v-for="(step, stepIndex) in steps" :key="step.step">
          <progress-step :stepIndex="stepIndex" :step="step" class="step" @specific-step="specificStep">
          </progress-step>
        </div>
      </div>

      <v-btn
        width="105"
        height="110"
        class="mb-0 text-capitalize sigfloButtonSmall400 nextButton mt-1"
        :color="getArrowColor"
        id="nextArrow"
        dark
        :disabled="!showNextButton"
        @click="nextStep"
      >
        <v-icon small>{{ nextButtonIcon }}</v-icon
        >{{ nextButtonText }}
      </v-btn>
    </v-footer>
  </div>
</template>

<script>
  import { mapState } from 'vuex';
  import progressStep from './FooterProgressStep.vue';

  export default {
    components: {
      progressStep
    },
    props: {
      steps: {}
    },
    mounted() {},
    computed: {
      ...mapState(['configurator', 'navigation']),
      /**
       * Returns the text for the next button based on the current step index.
       * If the current step index is the last step, it returns 'Finish',
       * otherwise it returns 'Next'.
       *
       * @returns {string} The text for the next button.
       */
      nextButtonText() {
        return this.isLastStep(this.getCurrentStepIndex()) && !this.isSettingsOnlyStep ? 'Finish' : 'Next';
      },
      /**
       * Returns the icon name for the next button based on the current step index.
       * If the current step is the last step, returns 'mdi-check', otherwise returns 'mdi-arrow-right'.
       *
       * @returns {string} The icon name for the next button.
       */
      nextButtonIcon() {
        return this.getCurrentStepIndex() === this.steps.length - 1 ? 'mdi-check' : 'mdi-arrow-right';
      },
      /**
       * Returns TRUE if no product is selected
       */
      noSelectedProduct() {
        const hasProducts =
          this.configurator.currentSubsystemStepComponent &&
          this.configurator.currentSubsystemStepComponent.productModels.length > 0;
        const selectedProduct = this.configurator.currentSubsystemStepComponent
          ? this.configurator.currentSubsystemStepComponent.productModels.find((x) => x.selected)
          : false;
        return hasProducts && !selectedProduct;
      },
      /**
       * returns TRUE if a required setting is not selected or is unconfigured
       */
      emptyRequiredSetting() {
        if (
          this.configurator.currentSubsystemStepComponent &&
          this.configurator.currentSubsystemStepComponent.componentSettingModels
        ) {
          return (
            this.configurator.currentSubsystemStepComponent.componentSettingModels.find(
              (x) => x.required && (x.value == null || x.value == '')
            ) != null
          );
        }
        return false;
      },
      invalidProductConfiguration() {
        // check for unselected/unconfigured required accessories
        let invalidProductSetting = [];
        if (!this.noSelectedProduct) {
          const selectedProduct = this.configurator.currentSubsystemStepComponent
            ? this.configurator.currentSubsystemStepComponent.productModels.find((x) => x.selected)
            : null;
          if (selectedProduct) {
            invalidProductSetting = selectedProduct.productSettingModels.filter(
              (x) => x.value == null || x.value == ''
            );
          }
        }
        return invalidProductSetting.length > 0;
      },
      /**
       * returns TRUE if a required accessory is not selected or is unconfigured
       */
      invalidRequiredAccessory() {
        // check for unselected/unconfigured required accessories
        let invalidRequiredAccessory = [];
        if (!this.noSelectedProduct) {
          const selectedProduct = this.configurator.currentSubsystemStepComponent
            ? this.configurator.currentSubsystemStepComponent.productModels.find((x) => x.selected)
            : null;
          if (selectedProduct) {
            // find any required accessory not selected
            invalidRequiredAccessory = selectedProduct.productAccessoryModels.filter(
              (x) => x.required && !x.selected && !x.customerSupplied
            );
            // find any selected accessory not fully configured
            const unconfiguredAccessory = selectedProduct.productAccessoryModels
              .filter((x) => x.selected && !x.customerSupplied)
              .filter((x) => x.productSettingModels.some((setting) => setting.value == null));
            // add any unconfigured accessories to the invalidRequiredAccessory array
            invalidRequiredAccessory = invalidRequiredAccessory.concat(unconfiguredAccessory);
          }
        }
        return invalidRequiredAccessory.length > 0;
      },
      /**
       * Returns color to use for the arrow based on business logic
       */
      getArrowColor() {
        return this.noSelectedProduct ||
          this.invalidRequiredAccessory ||
          this.emptyRequiredSetting ||
          this.productNotConfigured ||
          this.anySelectedAccessoryNotConfigured
          ? 'red'
          : 'green';
      },
      showPrevButton: function () {
        return this.getCurrentStepIndex() > 0;
      },
      showNextButton: function () {
        return true;
      }
    },
    watch: {
      'configurator.currentSubsystemStepComponent': function () {
        this.$nextTick(() => {
          const percentage = (this.getCurrentStepIndex() / this.configurator.currentSubsystemSteps.length) * 100;
          this.scrollByPercentage(percentage);
        });
      }
    },
    methods: {
      /**
       * Scrolls the footer by a given percentage.
       *
       * @param {number} percentage - The percentage by which to scroll the footer.
       */
      scrollByPercentage: function (percentage) {
        const footer = this.$refs.scrollableFooter;
        const maxScrollLeft = footer.scrollWidth - footer.clientWidth; // subtract 340 for margins
        const scrollAmount = (percentage / 100) * maxScrollLeft;

        // Ensure that the scroll position does not go beyond the limits
        const newScrollLeft = Math.min(Math.max(footer.scrollLeft + scrollAmount, 0), maxScrollLeft);
        footer.scrollLeft = scrollAmount;
        console.log('scrolling to', newScrollLeft);
      },
      /**
       * Proceeds to next step
       */
      nextStep: function () {
        this.$eventBus.$emit('component-step-navigation', 'next'); // fires validation on forms to highlight errors (does not stop execution)
        this.saveAndGoToStep(this.getCurrentStepIndex() + 1);
      },
      /**
       * Navigates to the previous step in the component.
       * Emits a 'component-step-navigation' event to notify other components of the navigation.
       * Updates the current step index by subtracting 1 from the current step index.
       */
      previousStep: function () {
        this.$eventBus.$emit('component-step-navigation', 'previous');
        this.saveAndGoToStep(this.getCurrentStepIndex() - 1);
      },
      /**
       * Navigates to a specific step
       */
      specificStep: function (stepIndex) {
        this.$eventBus.$emit('component-step-navigation', 'specific');
        this.saveAndGoToStep(stepIndex);
      }
    }
  };
</script>

<style scoped>
  .theme--dark.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
    border: 1px solid #200c40 !important;
    background-color: #200c40 !important;
    color: #fafafb !important;
  }
  .footerContainer {
    width: calc(100% - 256px);
    height: 142px;
    background-color: #200c40;
  }
  .footerContainerMobile {
    width: 100% !important;
  }
  .footerCap {
    min-height: 25px;
    background-color: #f4f2f7;
    border-bottom-left-radius: 25px;
  }
  .footer {
    width: 100%;
    position: relative;
    z-index: 5;
  }
  .footerDiv {
    overflow-x: auto;
    white-space: nowrap;
    overflow-y: hidden;
    width: 100%;
    margin-left: 160px;
    margin-right: 180px;
    justify-content: space-between;

    max-height: 110px;
  }

  .footerDiv::before {
    margin-left: 220px;
    margin-right: 220px;
    content: '';
    position: absolute;
    top: 37%;
    left: 0;
    right: 0;
    height: 2px;
    border-top: 2px dashed rgba(250, 250, 251, 0.5);
    transform: translateY(-50%);
    z-index: -1;
  }

  .timelineBottom {
    display: flex;
    /* position: absolute;
    top: 55px;
    left: 70px; */
    width: 100%;
    height: 142px;
    /* border-top: 2px dashed rgba(250, 250, 251, 0.5); */

    /* z-index: -1; */
  }

  .step {
    align-self: center;
    background-color: inherit;
    /* height: 50%; */
    padding-bottom: 33px;
    padding-left: 26px;
    /* width: 100px; */
    position: relative;
    border: 4px solid transparent;
    font-size: 14px;
    /* line-height: 1.6em; */
    z-index: 2;
  }
  .previousButton {
    position: absolute;
    left: 40px;
    top: 0px;
    z-index: 3;
  }
  .nextButton {
    position: absolute;
    right: 40px;
    top: 0px;
    z-index: 3;
  }

  .currentStep::before {
    content: '';
    position: absolute;
    width: 70%;
    height: 30px;
    top: 46px;
    left: 35px;
    border-top: 8px solid #4630c4;
    z-index: 1;
    padding: 0 0 0 6px;
  }
</style>
